import { useState } from 'react';
import { useTransfers, useCreateTransfer, useUpdateTransfer, useSubmitTransfer } from '../hooks/useTransfers';
import { useAccounts } from '../hooks/useAccounts';
import { useProducts } from '../hooks/useProducts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createTransferSchema, type Transfer, type CreateTransferInput, type Account, type Product } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { PlusIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function TransferPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading } = useTransfers(statusFilter || undefined, page);
  const { data: accounts } = useAccounts();
  const { data: products } = useProducts();
  const createMutation = useCreateTransfer();
  const updateMutation = useUpdateTransfer();
  const submitMutation = useSubmitTransfer();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Transfer | null>(null);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<CreateTransferInput>({
    resolver: zodResolver(createTransferSchema) as never,
    defaultValues: { is_ira_account: 'N' },
  });

  const isIRA = watch('is_ira_account');

  function openNew() {
    setEditing(null);
    reset({
      from_account_number: '', to_account_number: '', product_code: '',
      item_quantity: 1, is_ira_account: 'N', to_account_title: '',
      client_reference: '', to_ira_custodian: '',
    });
    setModalOpen(true);
  }

  function openEdit(row: Transfer) {
    if (row.status !== 'draft') return;
    setEditing(row);
    reset({
      from_account_number: row.from_account_number,
      to_account_number: row.to_account_number,
      to_account_title: row.to_account_title ?? '',
      product_code: row.product_code,
      item_quantity: row.item_quantity,
      is_ira_account: row.is_ira_account ?? 'N',
      to_ira_custodian: row.to_ira_custodian ?? '',
      client_reference: row.client_reference ?? '',
    });
    setModalOpen(true);
  }

  async function onSubmit(formData: CreateTransferInput) {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...formData });
        toast.success('Transfer updated');
      } else {
        await createMutation.mutateAsync(formData);
        toast.success('Transfer draft created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleSubmitToSFTP(id: number, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm('Generate Excel and upload to FideliTrade SFTP?')) return;
    try {
      await submitMutation.mutateAsync(id);
      toast.success('Transfer submitted to SFTP');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit');
    }
  }

  const transfers = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-silver-500">{pagination?.total ?? 0} transfers</p>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-silver-300 px-3 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {['draft', 'uploaded', 'processing', 'completed', 'exception', 'cancelled'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <Button onClick={openNew}><PlusIcon className="h-4 w-4" /> New Transfer</Button>
      </div>

      <Card padding={false}>
        <Table<Transfer>
          loading={isLoading}
          data={transfers}
          onRowClick={openEdit}
          columns={[
            { key: 'id', header: 'ID' },
            { key: 'from_account_number', header: 'From Account' },
            { key: 'to_account_number', header: 'To Account' },
            { key: 'product_code', header: 'Product' },
            { key: 'item_quantity', header: 'Qty' },
            { key: 'is_ira_account', header: 'IRA', render: (r) => r.is_ira_account === 'Y' ? 'Yes' : 'No' },
            { key: 'status', header: 'Status', render: (r) => <Badge>{r.status}</Badge> },
            {
              key: 'actions', header: '',
              render: (r) => r.status === 'draft' ? (
                <Button size="sm" onClick={(e) => handleSubmitToSFTP(r.id, e)} loading={submitMutation.isPending}>
                  <PaperAirplaneIcon className="h-4 w-4" /> Submit
                </Button>
              ) : null,
            },
          ]}
        />
        {pagination && (
          <div className="px-4">
            <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Transfer' : 'New Transfer'} size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="From Account"
              placeholder="Select source account..."
              options={(accounts ?? []).map((a: Account) => ({
                value: a.account_number, label: `${a.account_number} - ${a.title}`,
              }))}
              {...register('from_account_number')}
              error={errors.from_account_number?.message}
            />
            <Select
              label="To Account"
              placeholder="Select destination account..."
              options={(accounts ?? []).map((a: Account) => ({
                value: a.account_number, label: `${a.account_number} - ${a.title}`,
              }))}
              {...register('to_account_number')}
              error={errors.to_account_number?.message}
            />
          </div>
          <Input label="To Account Title" {...register('to_account_title')} />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Product"
              placeholder="Select product..."
              options={(products ?? []).map((p: Product) => ({
                value: p.product_code, label: `${p.product_code} - ${p.description}`,
              }))}
              {...register('product_code')}
              error={errors.product_code?.message}
            />
            <Input label="Quantity" type="number" step="any" {...register('item_quantity', { valueAsNumber: true })} error={errors.item_quantity?.message} />
          </div>
          <Input label="Client Reference" {...register('client_reference')} />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="IRA Account?"
              options={[{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }]}
              {...register('is_ira_account')}
            />
            {isIRA === 'Y' && (
              <Input label="IRA Custodian" {...register('to_ira_custodian')} error={errors.to_ira_custodian?.message} />
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Update Draft' : 'Save Draft'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
